import { useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// Icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplyIcon from '@mui/icons-material/Reply';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';

import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';
import apiClient from 'utils/apiClient';

const QUICK_TEMPLATES = [
  'Thank you for your wonderful review! We are thrilled you love your jewelry. ✨',
  'We appreciate your feedback! Shirish Sparkle Gems is dedicated to finest craftsmanship. 💎',
  'Thank you for shopping with us! Let us know if you ever need any assistance or care tips. 🤍',
  'We appreciate your feedback and apologize for any delay. Our concierge team is looking into this! 🙏'
];

const normalizeReply = (reply) => {
  if (!reply || typeof reply !== 'object') return null;
  const comment = String(reply.comment || '').trim();
  if (!comment) return null;
  return {
    ...reply,
    adminName: reply.adminName || 'Shirish Sparkle Concierge',
    comment
  };
};

const formatDate = (value) => {
  if (!value) return '-';
  try {
    const d = new Date(value);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return String(value);
  }
};

const getProductThumbnail = (product) => {
  if (!product) return null;
  if (Array.isArray(product.images) && product.images.length > 0) {
    const first = product.images[0];
    return typeof first === 'string' ? first : first?.url || first?.path || null;
  }
  return product.thumbnail || product.image || null;
};

const getAvatarColor = (name) => {
  const colors = ['#e05260', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i += 1) {
    hash = (name || '').charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export default function ProductReviews() {
  const theme = useTheme();

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [replyFilter, setReplyFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Reply Modal State
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [activeReview, setActiveReview] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isDeletingReply, setIsDeletingReply] = useState(false);

  // Status Alerts
  const [actionState, setActionState] = useState({ type: '', message: '' });

  // Query Endpoint
  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (search.trim()) params.set('q', search.trim());
    if (replyFilter !== 'all') params.set('replied', replyFilter);
    return `/api/products/admin/reviews?${params.toString()}`;
  }, [page, replyFilter, rowsPerPage, search]);

  const { data, error, loading, mutate } = useGet(endpoint);
  const rawReviews = Array.isArray(data?.reviews) ? data.reviews : [];

  // Client-side rating filter if chosen
  const filteredReviews = useMemo(() => {
    if (ratingFilter === 'all') return rawReviews;
    const targetRating = Number(ratingFilter);
    return rawReviews.filter((r) => Math.round(Number(r?.rating || 0)) === targetRating);
  }, [rawReviews, ratingFilter]);

  // Derived Analytics / KPIs
  const stats = useMemo(() => {
    const total = Number(data?.total || rawReviews.length || 0);
    if (!rawReviews.length) {
      return { total, avgRating: '5.0', awaitingReply: 0, repliedCount: 0, responseRate: '100%' };
    }

    const ratingsSum = rawReviews.reduce((sum, r) => sum + Number(r?.rating || 5), 0);
    const avgRating = (ratingsSum / rawReviews.length).toFixed(1);

    const repliedCount = rawReviews.filter((r) => normalizeReply(r?.adminReply)).length;
    const awaitingReply = rawReviews.filter((r) => !normalizeReply(r?.adminReply)).length;
    const responseRate = rawReviews.length ? `${Math.round((repliedCount / rawReviews.length) * 100)}%` : '100%';

    return { total, avgRating, awaitingReply, repliedCount, responseRate };
  }, [data?.total, rawReviews]);

  // Modal Handlers
  const handleOpenReplyModal = (review) => {
    setActiveReview(review);
    const existing = normalizeReply(review?.adminReply);
    setReplyDraft(existing?.comment || '');
    setReplyModalOpen(true);
  };

  const handleCloseReplyModal = () => {
    if (isSubmittingReply || isDeletingReply) return;
    setReplyModalOpen(false);
    setActiveReview(null);
    setReplyDraft('');
  };

  const handleSaveReply = async () => {
    const productId = activeReview?.product?._id;
    const reviewId = activeReview?._id;
    const comment = String(replyDraft || '').trim();

    if (!productId || !reviewId) return;
    if (!comment) {
      setActionState({ type: 'error', message: 'Please enter a reply before saving.' });
      return;
    }

    setIsSubmittingReply(true);
    setActionState({ type: '', message: '' });

    try {
      await apiClient.patch(`/api/products/${productId}/reviews/${reviewId}/reply`, { comment });
      setActionState({ type: 'success', message: 'Official reply published successfully.' });
      handleCloseReplyModal();
      await mutate();
    } catch (actionError) {
      setActionState({
        type: 'error',
        message: actionError?.response?.data?.message || 'Failed to publish reply.'
      });
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleDeleteReply = async () => {
    const productId = activeReview?.product?._id;
    const reviewId = activeReview?._id;
    if (!productId || !reviewId) return;

    setIsDeletingReply(true);
    setActionState({ type: '', message: '' });

    try {
      await apiClient.delete(`/api/products/${productId}/reviews/${reviewId}/reply`);
      setActionState({ type: 'success', message: 'Reply deleted successfully.' });
      handleCloseReplyModal();
      await mutate();
    } catch (actionError) {
      setActionState({
        type: 'error',
        message: actionError?.response?.data?.message || 'Failed to delete reply.'
      });
    } finally {
      setIsDeletingReply(false);
    }
  };

  const hasActiveFilters = Boolean(search.trim() || replyFilter !== 'all' || ratingFilter !== 'all');

  const handleResetFilters = () => {
    setSearch('');
    setReplyFilter('all');
    setRatingFilter('all');
    setPage(0);
  };

  return (
    <Stack spacing={3}>
      {/* 1. Header with Title, Tagline & Refresh */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}>
            Product Reviews
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Monitor customer feedback, assess satisfaction ratings, and publish official brand replies.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => mutate()}
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 2,
              textTransform: 'none',
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) }
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
      </Box>

      {/* 2. KPI Metrics Bar */}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
                theme.palette.background.paper,
                1
              )} 100%)`,
              transition: 'all 0.25s ease-in-out',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[2] }
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Reviews
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, color: 'text.primary' }}>
                  {stats.total}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  width: 48,
                  height: 48,
                  borderRadius: 2.5
                }}
              >
                <RateReviewOutlinedIcon />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha('#f59e0b', 0.08)} 0%, ${alpha(
                theme.palette.background.paper,
                1
              )} 100%)`,
              transition: 'all 0.25s ease-in-out',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[2] }
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Average Rating
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 0.5 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#d97706' }}>
                    {stats.avgRating}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    / 5.0
                  </Typography>
                </Stack>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha('#f59e0b', 0.15),
                  color: '#d97706',
                  width: 48,
                  height: 48,
                  borderRadius: 2.5
                }}
              >
                <StarIcon />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: stats.awaitingReply > 0 ? alpha('#f43f5e', 0.3) : 'divider',
              background: stats.awaitingReply > 0
                ? `linear-gradient(135deg, ${alpha('#f43f5e', 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.grey[200], 0.4)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
              transition: 'all 0.25s ease-in-out',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[2] }
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Awaiting Reply
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: stats.awaitingReply > 0 ? '#e11d48' : 'text.primary' }}>
                    {stats.awaitingReply}
                  </Typography>
                  {stats.awaitingReply > 0 && (
                    <Chip
                      size="small"
                      label="Attention"
                      sx={{
                        bgcolor: alpha('#f43f5e', 0.12),
                        color: '#e11d48',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 20
                      }}
                    />
                  )}
                </Stack>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha('#f43f5e', 0.12),
                  color: '#e11d48',
                  width: 48,
                  height: 48,
                  borderRadius: 2.5
                }}
              >
                <AccessTimeIcon />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha('#10b981', 0.08)} 0%, ${alpha(
                theme.palette.background.paper,
                1
              )} 100%)`,
              transition: 'all 0.25s ease-in-out',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[2] }
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Response Rate
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, color: '#059669' }}>
                  {stats.responseRate}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha('#10b981', 0.12),
                  color: '#059669',
                  width: 48,
                  height: 48,
                  borderRadius: 2.5
                }}
              >
                <CheckCircleOutlineIcon />
              </Avatar>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* 3. Search & Filter Bar */}
      <Card
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by product title, SKU, customer name, or keywords..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 2.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Reply Status"
              value={replyFilter}
              onChange={(e) => {
                setReplyFilter(e.target.value);
                setPage(0);
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="false">⏳ Awaiting reply</MenuItem>
              <MenuItem value="true">✅ Replied</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 2.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Rating"
              value={ratingFilter}
              onChange={(e) => {
                setRatingFilter(e.target.value);
                setPage(0);
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="all">All ratings</MenuItem>
              <MenuItem value="5">⭐⭐⭐⭐⭐ (5 Stars)</MenuItem>
              <MenuItem value="4">⭐⭐⭐⭐ (4 Stars)</MenuItem>
              <MenuItem value="3">⭐⭐⭐ (3 Stars)</MenuItem>
              <MenuItem value="2">⭐⭐ (2 Stars)</MenuItem>
              <MenuItem value="1">⭐ (1 Star)</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<FilterAltOutlinedIcon />}
              onClick={handleResetFilters}
              disabled={!hasActiveFilters}
              sx={{
                height: 40,
                borderRadius: 2,
                textTransform: 'none',
                color: hasActiveFilters ? 'primary.main' : 'text.secondary',
                borderColor: hasActiveFilters ? 'primary.main' : 'divider'
              }}
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Status Notifications */}
      {actionState.message && (
        <Alert
          severity={actionState.type === 'error' ? 'error' : 'success'}
          onClose={() => setActionState({ type: '', message: '' })}
          sx={{ borderRadius: 2 }}
        >
          {actionState.message}
        </Alert>
      )}

      {/* 4. Table Card */}
      <MainCard
        content={false}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: alpha(theme.palette.grey[100] || '#f8fafc', 0.8) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, py: 1.75, width: '28%' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 1.75, width: '18%' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 1.75, width: '14%' }}>Rating</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 1.75, width: '26%' }}>Review & Admin Reply</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, py: 1.75, width: '14%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && !filteredReviews.length ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Loading verified reviews...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Alert severity="error" sx={{ display: 'inline-flex' }}>
                      Failed to load reviews from server. Please try refreshing.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : filteredReviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <Box sx={{ maxWidth: 360, mx: 'auto', textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          mx: 'auto',
                          mb: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main'
                        }}
                      >
                        <RateReviewOutlinedIcon fontSize="medium" />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        No reviews found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {hasActiveFilters
                          ? 'No customer reviews matched your search and filter criteria. Try resetting the filters.'
                          : 'There are currently no customer reviews registered for your products.'}
                      </Typography>
                      {hasActiveFilters && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleResetFilters}
                          sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReviews.map((review) => {
                  const reviewId = review?._id || '';
                  const reply = normalizeReply(review?.adminReply);
                  const thumbnail = getProductThumbnail(review?.product);
                  const ratingNum = Number(review?.rating || 0);

                  return (
                    <TableRow
                      key={reviewId}
                      hover
                      sx={{
                        transition: 'background-color 0.15s ease',
                        '&:last-child td, &:last-child th': { border: 0 }
                      }}
                    >
                      {/* Product details */}
                      <TableCell sx={{ verticalAlign: 'top', py: 2 }}>
                        <Stack direction="row" spacing={1.75} alignItems="flex-start">
                          <Avatar
                            variant="rounded"
                            src={thumbnail}
                            alt={review?.product?.title || 'Product'}
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              color: 'primary.main',
                              flexShrink: 0
                            }}
                          >
                            <DiamondOutlinedIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Tooltip title={review?.product?.title || '-'} placement="top">
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: 'text.primary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.3
                                }}
                              >
                                {review?.product?.title || 'Untitled Product'}
                              </Typography>
                            </Tooltip>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                              {review?.product?.SKU && (
                                <Chip
                                  size="small"
                                  label={`SKU: ${review.product.SKU}`}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.68rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.grey[500], 0.08)
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Customer info */}
                      <TableCell sx={{ verticalAlign: 'top', py: 2 }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '0.82rem',
                              fontWeight: 700,
                              bgcolor: getAvatarColor(review?.userName || 'Buyer')
                            }}
                          >
                            {(review?.userName || 'B').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {review?.userName || 'Verified Buyer'}
                              </Typography>
                              <Tooltip title="Verified Customer" placement="top">
                                <VerifiedIcon sx={{ fontSize: 15, color: '#0ea5e9' }} />
                              </Tooltip>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatDate(review?.createdAt || review?.updatedAt)}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Rating */}
                      <TableCell sx={{ verticalAlign: 'top', py: 2 }}>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Rating value={ratingNum} precision={0.5} readOnly size="small" />
                          <Chip
                            size="small"
                            label={`${ratingNum.toFixed(1)} / 5.0`}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              bgcolor:
                                ratingNum >= 4
                                  ? alpha('#10b981', 0.12)
                                  : ratingNum >= 3
                                  ? alpha('#f59e0b', 0.12)
                                  : alpha('#ef4444', 0.12),
                              color:
                                ratingNum >= 4
                                  ? '#059669'
                                  : ratingNum >= 3
                                  ? '#d97706'
                                  : '#dc2626'
                            }}
                          />
                        </Stack>
                      </TableCell>

                      {/* Review & Reply */}
                      <TableCell sx={{ verticalAlign: 'top', py: 2 }}>
                        <Box sx={{ mb: 1 }}>
                          {review?.title && (
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                              {review.title}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.5,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {review?.comment || 'No written text provided.'}
                          </Typography>
                        </Box>

                        {/* Admin Reply Card Preview */}
                        {reply ? (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.25,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: alpha(theme.palette.primary.main, 0.25),
                              bgcolor: alpha(theme.palette.primary.main, 0.03),
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.07),
                                borderColor: 'primary.main'
                              }
                            }}
                            onClick={() => handleOpenReplyModal(review)}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                  {reply.adminName}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(reply.updatedAt || reply.createdAt)}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.81rem',
                                color: 'text.primary',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {reply.comment}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                            size="small"
                            label="Awaiting Reply"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              borderColor: alpha('#f59e0b', 0.5),
                              color: '#d97706',
                              bgcolor: alpha('#f59e0b', 0.05)
                            }}
                          />
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" sx={{ verticalAlign: 'top', py: 2 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title={reply ? 'Edit official response' : 'Write official reply'}>
                            <Button
                              size="small"
                              variant={reply ? 'outlined' : 'contained'}
                              color="primary"
                              startIcon={reply ? <EditOutlinedIcon /> : <ReplyIcon />}
                              onClick={() => handleOpenReplyModal(review)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                px: 1.5
                              }}
                            >
                              {reply ? 'Edit' : 'Reply'}
                            </Button>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Footer */}
        <Divider />
        <TablePagination
          component="div"
          count={Number(data?.total || filteredReviews.length || 0)}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 15, 25, 50]}
        />
      </MainCard>

      {/* 5. Executive Reply Modal Dialog with Quick-Templates */}
      <Dialog
        open={replyModalOpen}
        onClose={handleCloseReplyModal}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3, overflow: 'hidden' }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {normalizeReply(activeReview?.adminReply) ? 'Edit Official Brand Response' : 'Reply to Customer Review'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Publish a verified store response visible to all store visitors.
              </Typography>
            </Box>
            <IconButton onClick={handleCloseReplyModal} size="small" disabled={isSubmittingReply || isDeletingReply}>
              <ClearIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          <Stack spacing={2.5}>
            {/* Customer Review Summary Card */}
            {activeReview && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      bgcolor: getAvatarColor(activeReview?.userName || 'Buyer')
                    }}
                  >
                    {(activeReview?.userName || 'B').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {activeReview?.userName || 'Verified Buyer'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(activeReview?.createdAt)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                      <Rating value={Number(activeReview?.rating || 5)} precision={0.5} readOnly size="small" />
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {activeReview?.product?.title || 'Product'}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
                {activeReview?.title && (
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    "{activeReview.title}"
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
                  "{activeReview?.comment || 'No written text.'}"
                </Typography>
              </Box>
            )}

            {/* Quick Response Templates */}
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Quick Response Templates (Click to insert)
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {QUICK_TEMPLATES.map((tmpl, idx) => (
                  <Chip
                    key={idx}
                    label={tmpl}
                    variant="outlined"
                    onClick={() => setReplyDraft(tmpl)}
                    sx={{
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: 'primary.main'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Official Store Reply Field */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Official Store Reply
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                placeholder="Write an authentic, professional response representing Shirish Sparkle Gems..."
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
                {replyDraft.length} characters
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          {normalizeReply(activeReview?.adminReply) ? (
            <Button
              color="error"
              variant="text"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDeleteReply}
              disabled={isSubmittingReply || isDeletingReply}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {isDeletingReply ? 'Deleting...' : 'Delete Reply'}
            </Button>
          ) : (
            <Box />
          )}

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              onClick={handleCloseReplyModal}
              disabled={isSubmittingReply || isDeletingReply}
              sx={{ borderRadius: 2, textTransform: 'none', px: 2.5 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveReply}
              disabled={isSubmittingReply || isDeletingReply || !replyDraft.trim()}
              startIcon={isSubmittingReply ? <CircularProgress size={16} color="inherit" /> : <ReplyIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3, fontWeight: 600 }}
            >
              {isSubmittingReply ? 'Saving...' : 'Publish Response'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

